import { useState, useEffect, useMemo } from "react";
import { openExternalLink } from "@/lib/utils";
import { gigMusicianIds } from "@/lib/socialTags";
import { Layout } from "@/pages/Dashboard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Sparkles, Copy, Check, Loader2, Instagram, Facebook, ExternalLink, RotateCcw } from "lucide-react";
import { apiClient, useAuth } from "@/App";
import { toast } from "sonner";
import { format, parseISO, startOfMonth, endOfMonth, subMonths, addDays, isWithinInterval } from "date-fns";
import { Link } from "react-router-dom";
import { copyToClipboard as copyText } from "@/lib/clipboard";

const FILTER_OPTIONS = [
  { key: "manual", label: "Manual" },
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "next_week", label: "Next week" },
  { key: "tour", label: "Tour" },
];

const DEFAULT_HASHTAGS = "#jazz #livemusic #fretless";
const DEFAULT_OPENING_LINE = "What a month of music! 🎺";

// Toggle defaults — match spec.
const DEFAULT_IMAGE_TOGGLES = {
  date: true,
  venue: true,
  city: true,
  time: false,
  band_handle: false,
  collaborator_handles: false,
  venue_handle: false,
};

const DEFAULT_CAPTION_TOGGLES = {
  band_name: true,
  band_handle: true,
  collaborator_handles: false,
  venue_handle: true,
  hashtags: true,
  ticket_link: true,
};

const formatHandle = (h) => {
  if (!h) return "";
  const trimmed = String(h).trim().replace(/^@/, "");
  return trimmed ? `@${trimmed}` : "";
};

// Custom X (formerly Twitter) glyph — matches the one used in SocialGenerator
// so the visual identity stays consistent across the two pages.
const XLogo = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const PLATFORM_OPTIONS = [
  { key: "instagram", label: "Instagram", icon: (cn) => <Instagram className={cn} />, url: "https://www.instagram.com" },
  { key: "facebook",  label: "Facebook",  icon: (cn) => <Facebook  className={cn} />, url: "https://www.facebook.com" },
  { key: "twitter",   label: "X",         icon: (cn) => <XLogo     className={cn} />, url: "https://x.com" },
];

const shortDate = (iso) => {
  if (!iso) return "";
  try {
    return format(parseISO(iso), "EEE d MMM");
  } catch {
    return iso.slice(0, 10);
  }
};

export const BatchSocialGenerator = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [gigs, setGigs] = useState([]);
  const [bands, setBands] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [venues, setVenues] = useState([]);
  const [tours, setTours] = useState([]);
  const [savedHashtags, setSavedHashtags] = useState("");

  // Selection state
  const [filter, setFilter] = useState("manual");
  const [selectedTourId, setSelectedTourId] = useState("");
  const [selectedGigIds, setSelectedGigIds] = useState([]);

  // Toggles
  const [imageToggles, setImageToggles] = useState(DEFAULT_IMAGE_TOGGLES);
  const [captionToggles, setCaptionToggles] = useState(DEFAULT_CAPTION_TOGGLES);
  const [openingLine, setOpeningLine] = useState(DEFAULT_OPENING_LINE);

  // Editable output state — initialised from the auto-built strings, then
  // selectively updated based on whether the user has hand-edited.
  const [editedImageText, setEditedImageText] = useState("");
  const [editedCaptionText, setEditedCaptionText] = useState("");

  // "Has the user hand-edited this textarea?" flags. Once true, toggle changes
  // (which re-run imageText / captionText useMemos) will NOT clobber the
  // textarea — only a gig-selection change resets the flag and the buffer.
  const [userHasEditedImage, setUserHasEditedImage] = useState(false);
  const [userHasEditedCaption, setUserHasEditedCaption] = useState(false);

  // Active platform for the "Copy & Open" launcher under the caption window.
  const [batchPlatform, setBatchPlatform] = useState("instagram");

  // Copy feedback
  const [copied, setCopied] = useState({ image: false, caption: false, launcher: false });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [gigsRes, bandsRes, collabsRes, venuesRes, toursRes] = await Promise.all([
          apiClient.get("/gigs"),
          apiClient.get("/bands"),
          apiClient.get("/collaborators"),
          apiClient.get("/venues"),
          apiClient.get("/tours"),
        ]);
        if (cancelled) return;
        // Only public gigs are eligible for social posts.
        const publicGigs = (gigsRes.data || [])
          .filter(g => g.is_public !== false)
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        setGigs(publicGigs);
        setBands(bandsRes.data || []);
        setCollaborators(collabsRes.data || []);
        setVenues(venuesRes.data || []);
        setTours(toursRes.data || []);

        // Try to fetch user's saved hashtags from social_style settings.
        // Endpoint may not exist for every user — silently fall back.
        try {
          const ssRes = await apiClient.get("/social/style");
          const stored = ssRes.data?.preferred_hashtags || ssRes.data?.hashtags;
          if (stored) setSavedHashtags(String(stored).trim());
        } catch {
          // Silent — defaults will be used.
        }
      } catch (err) {
        console.error("Batch generator load failed:", err);
        toast.error("Failed to load gigs");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Apply quick-select filter → derive ticked gig IDs.
  // Manual filter never auto-changes the selection so the user can curate freely.
  useEffect(() => {
    if (filter === "manual") return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let ids = [];
    if (filter === "this_month") {
      const from = startOfMonth(today);
      const to = endOfMonth(today);
      ids = gigs.filter(g => {
        try { return isWithinInterval(parseISO(g.date), { start: from, end: to }); }
        catch { return false; }
      }).map(g => g.gig_id);
    } else if (filter === "last_month") {
      const lm = subMonths(today, 1);
      const from = startOfMonth(lm);
      const to = endOfMonth(lm);
      ids = gigs.filter(g => {
        try { return isWithinInterval(parseISO(g.date), { start: from, end: to }); }
        catch { return false; }
      }).map(g => g.gig_id);
    } else if (filter === "next_week") {
      const to = addDays(today, 7);
      ids = gigs.filter(g => {
        try {
          const d = parseISO(g.date);
          return d >= today && d <= to;
        } catch { return false; }
      }).map(g => g.gig_id);
    } else if (filter === "tour" && selectedTourId) {
      ids = gigs.filter(g => g.tour_id === selectedTourId).map(g => g.gig_id);
    }
    setSelectedGigIds(ids);
  }, [filter, selectedTourId, gigs]);

  const toggleGig = (id) => {
    setSelectedGigIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Lookup helpers — keyed for O(1) access while building outputs.
  const venueByName = useMemo(() => {
    const m = new Map();
    (venues || []).forEach(v => { if (v?.name) m.set(v.name.toLowerCase(), v); });
    return m;
  }, [venues]);

  const bandByName = useMemo(() => {
    const m = new Map();
    (bands || []).forEach(b => { if (b?.name) m.set(b.name.toLowerCase(), b); });
    return m;
  }, [bands]);

  const collabById = useMemo(() => {
    const m = new Map();
    (collaborators || []).forEach(c => { if (c?.collaborator_id) m.set(c.collaborator_id, c); });
    return m;
  }, [collaborators]);

  // Resolve the band record for a gig — match by gig.band_id first, then by artist_name.
  const bandForGig = (gig) => {
    if (!gig) return null;
    if (gig.band_id) {
      const direct = (bands || []).find(b => b.band_id === gig.band_id);
      if (direct) return direct;
    }
    if (gig.artist_name) return bandByName.get(gig.artist_name.toLowerCase()) || null;
    return null;
  };

  // Instagram handles of the musicians on this gig. Falls back to the band's roster
  // for older gigs that have nobody attached.
  const collabHandlesForGig = (gig) => {
    let ids = gigMusicianIds(gig);
    if (!ids.length) {
      const band = bandForGig(gig);
      ids = Array.isArray(band?.member_ids) ? band.member_ids : [];
    }
    return ids
      .map(id => collabById.get(id))
      .filter(c => c && c.instagram_handle && String(c.instagram_handle).trim())
      .map(c => formatHandle(c.instagram_handle));
  };

  // The venue's Instagram handle, if one is saved in Scene.
  const venueHandleForGig = (gig) => {
    const venue = venueByName.get((gig?.venue_name || "").toLowerCase());
    return venue?.instagram_handle ? formatHandle(venue.instagram_handle) : "";
  };

  // ── Window 1: Image text — minimal lines for photo overlay ──
  const buildImageText = () => {
    const lines = [];
    const selected = (gigs || []).filter(g => selectedGigIds.includes(g.gig_id));
    for (const gig of selected) {
      const parts = [];
      if (imageToggles.date && gig.date) parts.push(shortDate(gig.date));
      // "Band @ Venue" body — only the parts the user toggled on.
      const bandLabel = gig.artist_name || "";
      const venueLabel = gig.venue_name || "";
      if (imageToggles.venue && bandLabel && venueLabel) {
        parts.push(`${bandLabel} @ ${venueLabel}`);
      } else if (bandLabel) {
        parts.push(bandLabel);
      } else if (imageToggles.venue && venueLabel) {
        parts.push(venueLabel);
      }
      if (imageToggles.city) {
        const venue = venueByName.get((gig.venue_name || "").toLowerCase());
        const city = gig.city || venue?.city;
        if (city) parts.push(city);
      }
      if (imageToggles.time && gig.start_time) parts.push(gig.start_time);
      if (parts.length) lines.push(parts.join(" — "));

      if (imageToggles.band_handle) {
        const band = bandForGig(gig);
        const handle = band?.instagram_handle ? formatHandle(band.instagram_handle) : (gig.artist_name || "");
        if (handle) lines.push(handle);
      }
      if (imageToggles.collaborator_handles) {
        const handles = collabHandlesForGig(gig);
        for (const h of handles) lines.push(h);
      }
      if (imageToggles.venue_handle) {
        const vh = venueHandleForGig(gig);
        if (vh) lines.push(vh);
      }
    }
    return lines.join("\n");
  };

  // ── Window 2: Caption — Instagram-ready ──
  const buildCaption = () => {
    const lines = [];
    if (openingLine.trim()) lines.push(openingLine.trim());
    const selected = (gigs || []).filter(g => selectedGigIds.includes(g.gig_id));

    for (const gig of selected) {
      const band = bandForGig(gig);
      const bandHandle = band?.instagram_handle ? formatHandle(band.instagram_handle) : "";
      // Build the venue tail with city if present (caption always shows city when available).
      const venue = venueByName.get((gig.venue_name || "").toLowerCase());
      const city = gig.city || venue?.city || "";
      const venueHandle = captionToggles.venue_handle ? venueHandleForGig(gig) : "";
      const venueName = gig.venue_name ? (venueHandle ? `${gig.venue_name} (${venueHandle})` : gig.venue_name) : "";
      const venueTail = venueName
        ? (city ? `${venueName}, ${city}` : venueName)
        : (city || "");

      let leadName = "";
      if (captionToggles.band_handle && bandHandle) {
        leadName = bandHandle;
      } else if (captionToggles.band_name && gig.artist_name) {
        leadName = gig.artist_name;
      } else if (gig.artist_name) {
        // Fallback when both toggles off → still show band name so the line is informative.
        leadName = gig.artist_name;
      }

      const bodyLine = [leadName, venueTail].filter(Boolean).join(" @ ");
      if (bodyLine) lines.push(bodyLine);

      if (captionToggles.collaborator_handles) {
        const handles = collabHandlesForGig(gig);
        if (handles.length) lines.push(handles.join(" "));
      }

      if (captionToggles.ticket_link && gig.ticket_link) {
        lines.push(gig.ticket_link);
      }
    }

    if (captionToggles.hashtags) {
      const tags = (savedHashtags && savedHashtags.trim()) || DEFAULT_HASHTAGS;
      lines.push(tags);
    }
    return lines.join("\n");
  };

  const imageText = useMemo(buildImageText, [selectedGigIds, gigs, imageToggles, venues, bands, collaborators]); // eslint-disable-line react-hooks/exhaustive-deps
  const captionText = useMemo(buildCaption, [selectedGigIds, gigs, captionToggles, openingLine, savedHashtags, venues, bands, collaborators]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset on gig selection change — always wipes hand edits in BOTH buffers,
  // refills with the freshly computed strings, and clears the "edited" flags
  // so subsequent toggle changes can re-flow into the textareas again.
  useEffect(() => {
    setEditedImageText(imageText);
    setEditedCaptionText(captionText);
    setUserHasEditedImage(false);
    setUserHasEditedCaption(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGigIds]);

  // Toggle change → image text recomputed → only push into the textarea if
  // the user hasn't hand-edited it. Otherwise their version is preserved.
  useEffect(() => {
    if (!userHasEditedImage) setEditedImageText(imageText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageText]);

  // Toggle change OR opening-line change → caption recomputed → same rule.
  // Note: the opening-line input also drives `captionText` via useMemo, so a
  // change there flows through this effect — meaning a change to the opening
  // line BEFORE the user hand-edits the caption will update the buffer (flag
  // is false, allowed), but AFTER a hand-edit the user's version is preserved
  // (flag is true). This matches Fix 3's requirement automatically.
  useEffect(() => {
    if (!userHasEditedCaption) setEditedCaptionText(captionText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captionText]);

  const copyOne = async (which, text) => {
    if (!text) {
      toast.error("Nothing to copy yet");
      return;
    }
    const ok = await copyText(text);
    if (!ok) {
      toast.error("Couldn't access clipboard");
      return;
    }
    setCopied(s => ({ ...s, [which]: true }));
    setTimeout(() => setCopied(s => ({ ...s, [which]: false })), 1500);
    toast.success("Copied!");
  };

  const renderToggle = (group, key, label, setter) => (
    <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
      <Checkbox
        checked={!!group[key]}
        onCheckedChange={(v) => setter(s => ({ ...s, [key]: !!v }))}
        data-testid={`batch-toggle-${key}`}
      />
      <span>{label}</span>
    </label>
  );

  // Shared platform launcher — used under BOTH Window 1 (image text) and
  // Window 2 (caption). Switching the active platform on either tab strip
  // updates the shared `batchPlatform` state, so both stay in sync.
  // `which` is a string used in toast messages + as a data-testid suffix.
  // `getText` is a getter so we always read the LATEST textarea value at
  // click-time, not a stale closure.
  const renderPlatformLauncher = (which, getText) => (
    <div className="pt-2 space-y-2">
      <div
        className="flex gap-1 p-1 bg-secondary/40 rounded-lg w-fit"
        data-testid={`batch-platform-tabs-${which}`}
      >
        {PLATFORM_OPTIONS.map(p => {
          const active = batchPlatform === p.key;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => setBatchPlatform(p.key)}
              className={`flex items-center justify-center w-9 h-9 rounded-md transition-colors ${active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"}`}
              aria-label={p.label}
              aria-pressed={active}
              data-testid={`batch-platform-${which}-${p.key}`}
            >
              {p.icon("h-4 w-4")}
            </button>
          );
        })}
      </div>
      <Button
        type="button"
        className="w-full sm:w-auto justify-start"
        onClick={async () => {
          const target = PLATFORM_OPTIONS.find(p => p.key === batchPlatform);
          if (!target) return;
          const text = getText();
          if (!text) {
            toast.error("Nothing to copy yet");
            return;
          }
          const ok = await copyText(text);
          if (!ok) {
            toast.error("Couldn't access clipboard");
            return;
          }
          setCopied(s => ({ ...s, launcher: true }));
          setTimeout(() => setCopied(s => ({ ...s, launcher: false })), 1500);
          toast.success(`Copied — opening ${target.label}…`);
          openExternalLink(target.url);
        }}
        data-testid={`batch-copy-open-${which}`}
      >
        {(PLATFORM_OPTIONS.find(p => p.key === batchPlatform) || PLATFORM_OPTIONS[0]).icon("h-4 w-4")}
        <span className="ml-2">Copy & Open</span>
        <ExternalLink className="h-4 w-4 ml-auto" />
      </Button>
    </div>
  );

  return (
    <Layout>
      <div data-testid="batch-social-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-primary mb-2">Social Media</p>
            <h1 className="font-heading text-3xl font-bold tracking-tight">Batch Post Generator</h1>
            <p className="text-muted-foreground mt-2">
              Pick gigs, toggle what you want, copy the text. Pure client-side — no API calls per regenerate.
            </p>
          </div>
          <Link to="/social">
            <Button variant="outline" data-testid="back-to-social">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="batch-loading">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* ── Step 1: Gig selection ── */}
            <Card className="bg-card border-border" data-testid="batch-gig-selection">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  1. Pick gigs
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Filter bar */}
                <div className="flex flex-wrap gap-2" data-testid="batch-filter-bar">
                  {FILTER_OPTIONS.map(opt => (
                    <Button
                      key={opt.key}
                      type="button"
                      variant={filter === opt.key ? "default" : "outline"}
                      size="sm"
                      onClick={() => setFilter(opt.key)}
                      data-testid={`batch-filter-${opt.key}`}
                    >
                      {opt.label}
                    </Button>
                  ))}
                  {filter === "tour" && (
                    <Select value={selectedTourId} onValueChange={setSelectedTourId}>
                      <SelectTrigger className="w-56 h-9" data-testid="batch-tour-select">
                        <SelectValue placeholder={tours.length ? "Pick a tour" : "No tours yet"} />
                      </SelectTrigger>
                      <SelectContent>
                        {tours.map(t => (
                          <SelectItem key={t.tour_id} value={t.tour_id}>
                            {t.name}{typeof t.gig_count === "number" ? ` (${t.gig_count})` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {/* Gig list */}
                <div className="border border-border rounded-lg max-h-72 overflow-y-auto divide-y divide-border" data-testid="batch-gig-list">
                  {gigs.length === 0 ? (
                    <p className="p-4 text-sm text-muted-foreground">
                      No public gigs found.
                    </p>
                  ) : (
                    gigs.map(gig => {
                      const checked = selectedGigIds.includes(gig.gig_id);
                      return (
                        <label
                          key={gig.gig_id}
                          className="flex items-center gap-3 p-3 hover:bg-secondary/30 cursor-pointer transition-colors"
                          data-testid={`batch-gig-row-${gig.gig_id}`}
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleGig(gig.gig_id)}
                          />
                          <span className="font-mono text-xs text-muted-foreground w-20 shrink-0">
                            {shortDate(gig.date)}
                          </span>
                          <span className="text-sm font-medium truncate flex-1">
                            {gig.artist_name || "(no band)"}
                          </span>
                          <span className="text-sm text-muted-foreground truncate">
                            {gig.venue_name || ""}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>

                <Badge variant="outline" data-testid="batch-selected-count">
                  {selectedGigIds.length} gig{selectedGigIds.length === 1 ? "" : "s"} selected
                </Badge>
              </CardContent>
            </Card>

            {/* ── Step 2: Output toggles ── */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="bg-card border-border" data-testid="batch-image-toggles">
                <CardHeader>
                  <CardTitle className="text-base">2a. Image text fields</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {renderToggle(imageToggles, "date", "Date", setImageToggles)}
                  {renderToggle(imageToggles, "venue", "Venue", setImageToggles)}
                  {renderToggle(imageToggles, "city", "City", setImageToggles)}
                  {renderToggle(imageToggles, "time", "Time", setImageToggles)}
                  {renderToggle(imageToggles, "band_handle", "Band handle", setImageToggles)}
                  {renderToggle(imageToggles, "collaborator_handles", "Musician handles", setImageToggles)}
                  {renderToggle(imageToggles, "venue_handle", "Venue handle", setImageToggles)}
                </CardContent>
              </Card>
              <Card className="bg-card border-border" data-testid="batch-caption-toggles">
                <CardHeader>
                  <CardTitle className="text-base">2b. Caption fields</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {renderToggle(captionToggles, "band_name", "Band name", setCaptionToggles)}
                  {renderToggle(captionToggles, "band_handle", "Band handle", setCaptionToggles)}
                  {renderToggle(captionToggles, "collaborator_handles", "Musician handles", setCaptionToggles)}
                  {renderToggle(captionToggles, "venue_handle", "Venue handle", setCaptionToggles)}
                  {renderToggle(captionToggles, "hashtags", "Hashtags", setCaptionToggles)}
                  {renderToggle(captionToggles, "ticket_link", "Ticket link", setCaptionToggles)}
                </CardContent>
              </Card>
            </div>

            {/* ── Step 3: Two output windows ── */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Window 1 — Image text */}
              <Card className="bg-card border-border" data-testid="batch-image-output">
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-base">3a. Image text</CardTitle>
                  <div className="flex items-center gap-2">
                    {userHasEditedImage && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditedImageText(imageText);
                          setUserHasEditedImage(false);
                          toast.success("Image text reset to auto");
                        }}
                        data-testid="batch-reset-image"
                      >
                        <RotateCcw className="h-3.5 w-3.5 mr-1" />
                        Reset
                      </Button>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => copyOne("image", editedImageText)}
                      data-testid="batch-copy-image"
                    >
                      {copied.image ? <><Check className="h-4 w-4 mr-2" />Copied</> : <><Copy className="h-4 w-4 mr-2" />Copy</>}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <textarea
                    value={editedImageText}
                    onChange={(e) => {
                      if (!userHasEditedImage) toast("✏️ Image text is now in manual mode — toggles won't update it until you reset", { duration: 5000 });
                      setEditedImageText(e.target.value);
                      setUserHasEditedImage(true);
                    }}
                    rows={8}
                    className="w-full font-mono text-sm bg-secondary/30 rounded-md p-3 min-h-[10rem] border border-border focus:border-primary focus:outline-none whitespace-pre-wrap break-words resize-y"
                    data-testid="batch-image-textarea"
                  />

                  {renderPlatformLauncher("image", () => editedImageText)}
                </CardContent>
              </Card>

              {/* Window 2 — Caption */}
              <Card className="bg-card border-border" data-testid="batch-caption-output">
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-base">3b. Caption</CardTitle>
                  <div className="flex items-center gap-2">
                    {userHasEditedCaption && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditedCaptionText(captionText);
                          setUserHasEditedCaption(false);
                          toast.success("Caption reset to auto");
                        }}
                        data-testid="batch-reset-caption"
                      >
                        <RotateCcw className="h-3.5 w-3.5 mr-1" />
                        Reset
                      </Button>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => copyOne("caption", editedCaptionText)}
                      data-testid="batch-copy-caption"
                    >
                      {copied.caption ? <><Check className="h-4 w-4 mr-2" />Copied</> : <><Copy className="h-4 w-4 mr-2" />Copy</>}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="batch-opening-line" className="text-xs text-muted-foreground">
                      Opening line
                    </Label>
                    <Input
                      id="batch-opening-line"
                      value={openingLine}
                      onChange={(e) => setOpeningLine(e.target.value)}
                      data-testid="batch-opening-line"
                    />
                  </div>
                  <textarea
                    value={editedCaptionText}
                    onChange={(e) => {
                      if (!userHasEditedCaption) toast("✏️ Caption is now in manual mode — toggles won't update it until you reset", { duration: 5000 });
                      setEditedCaptionText(e.target.value);
                      setUserHasEditedCaption(true);
                    }}
                    rows={10}
                    className="w-full font-sans text-sm bg-secondary/30 rounded-md p-3 min-h-[10rem] border border-border focus:border-primary focus:outline-none whitespace-pre-wrap break-words resize-y"
                    data-testid="batch-caption-textarea"
                  />

                  {renderPlatformLauncher("caption", () => editedCaptionText)}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BatchSocialGenerator;
